import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { toastSuccess, toastError, toastInfo } from './ui/toast';
import ConfirmDialog from './ui/ConfirmDialog';
import { useUserRole } from './hooks/useUserRole';
import { assertWriteOk } from './utils/dbWrite';
import { analyzeGifAudit, checkDemoAddress, storageDemoAddress, storageDemoStill } from './utils/gifAuditChecks';
import ExerciseStill from './components/ui/ExerciseStill';
import { colors as theme } from './theme';

// Names of demo files in the public "exercises" bucket, offered when assigning one.
// The list is not proof a file is there: an address is asked for before it is saved.
const AVAILABLE_GIFS = [
  'abductor-stretch.gif',
  'abs-crunch-bench.gif',
  'all-fours-lateral-kick-outs.gif',
  'alternate-bicep-curl-dumbbell.gif',
  'alternate-elbow-to-knee-crunch.gif',
  'alternate-front-raise.gif',
  'alternate-hammer-curl-dumbbell.gif',
  'alternate-hand-reach-plank.gif',
  'alternate-leg-raise-plank.gif',
  'arnold-press.gif',
  'assault-bike.gif',
  'assisted-dip.gif',
  'assisted-pull-up.gif',
  'band-face-pull.gif',
  'back-extension-machine.gif',
  'back-pec-stretch.gif',
  'back-squat.gif',
  'back-supported-exercise-bike.gif',
  'band-reverse-fly.gif',
  'band-side-lying-leg-lift.gif',
  'band-side-walk.gif',
  'bar-side-bend.gif',
  'barbell-good-morning.gif',
  'battle-rope.gif',
  'bear-plank.gif',
  'belt-squat.gif',
  'bench-dip.gif',
  'bench-kick-back.gif',
  'bench-press-dumbbell.gif',
  'bend-over-row-barbell.gif',
  'bend-over-row-dumbbell.gif',
  'bend-reverse-fly-cable.gif',
  'bend-reverse-fly-dumbbell.gif',
  'bendover-row-cable.gif',
  'bendover-row-landmine.gif',
  'bendover-row-smithmachine.gif',
  'bent-knee-dumbbell-pullover.gif',
  'bicep-cable-curl.gif',
  'bicep-curl-barbell.gif',
  'bird-dog-plank.gif',
  'bird-dog.gif',
  'boat-yoga-pose.gif',
  'bodyweight-back-lunges.gif',
  'bodyweight-bulgarian-split-squat.gif',
  'bodyweight-front-lunges.gif',
  'bodyweight-front-raise-with-squat.gif',
  'bodyweight-good-morning.gif',
  'bodyweight-knee-extension.gif',
  'bodyweight-lateral-lunge.gif',
  'bodyweight-pistol-squat.gif',
  'pistol-squat-kettlebell.gif',
  'bodyweight-squat-and-reach.gif',
  'bodyweight-step-down.gif',
  'book-stretch.gif',
  'bulgarian-split-squat-barbell.gif',
  'bulgarian-split-squat-dumbbell.gif',
  'burpee.gif',
  'butt-kicks.gif',
  'cable-hip-adduction.gif',
  'cable-single-arm-high-row.gif',
  'calf-raise-horizontal-leg-press-machine.gif',
  'calf-raise-smith-machine.gif',
  'captains-chair.gif',
  'cat-and-cow.gif',
  'chest-fly-dumbbell.gif',
  'chest-fly-machine.gif',
  'chest-press-barbell.gif',
  'chest-press-machine.gif',
  'chest-press-smith-machine.gif',
  'chest-stretch.gif',
  'chin-tuck.gif',
  'chest-supported-reverse-fly.gif',
  'chest-supported-row-machine.gif',
  'chest-supported-row-plate-loaded.gif',
  'child-to-cobra-stretching.gif',
  'chin-ups.gif',
  'clamshell-band.gif',
  'close-grip-barbell-bench-press.gif',
  'close-grip-cable-row.gif',
  'close-grip-eez-bar-curl.gif',
  'close-grip-push-up-ball.gif',
  'concentration-curl.gif',
  'cross-body-cable-pulldown.gif',
  'cross-over-cable.gif',
  'crunch-leg-elevated.gif',
  'crunches.gif',
  'curtsy-lunge.gif',
  'dead-bug.gif',
  'deadlift-barbell.gif',
  'deadlift-dumbbell.gif',
  'deadlift-smith-machine.gif',
  'deadlift-trap-bar.gif',
  'decline-chest-fly-dumbbell.gif',
  'decline-chest-press-barbell.gif',
  'decline-chest-press-dumbbell.gif',
  'decline-chest-press-smith-machine.gif',
  'decline-leg-raise.gif',
  'decline-tricep-extension-dumbbell.gif',
  'deep-lunge-to-hamstring-stretch.gif',
  'dip-machine.gif',
  'dip.gif',
  'donkey-raise.gif',
  'drag-curl.gif',
  'dumbbell-hold-lateral-leg-raise.gif',
  'dynamic-chest-stretch.gif',
  'elliptical-trainer.gif',
  'exercise-bike.gif',
  'ez-bar-bicep-curl.gif',
  'ez-bar-preacher-curl.gif',
  'ez-bar-reverse-barbell-curl.gif',
  'ez-bar-tricep-curl.gif',
  'ez-bar-upright-row.gif',
  'face-pull-cable.gif',
  'farmers-walk.gif',
  'feet-elevated-push-up.gif',
  'flat-bench-chest-press-cable.gif',
  'floor-t-raise.gif',
  'floor-tricep-extension-dumbbell.gif',
  'flutter-kicks.gif',
  'frog-crunches.gif',
  'frog-glute-bridge.gif',
  'frog-sit-up.gif',
  'front-dumbbell-squat.gif',
  'front-lunges-dumbbell.gif',
  'front-raise-barbell.gif',
  'front-raise-cable.gif',
  'front-raise-dumbbell.gif',
  'front-squat-barbell.gif',
  'glute-bridge.gif',
  'glute-bridge-barbell.gif',
  'glute-kickback-machine.gif',
  'goblet-squat.gif',
  'hack-squat.gif',
  'hammer-curl-dumbbell.gif',
  'hamstring-stretch.gif',
  'hanging-leg-raise.gif',
  'hanging-leg-raises-toes-to-bar.gif',
  'hanging-side-crunches.gif',
  'high-knees.gif',
  'hip-thrust-barbell.gif',
  'hip-thrust-smith-machine.gif',
  'hollow-body-hold.gif',
  'horizontal-leg-press-calf-raise.gif',
  'horizontal-leg-press.gif',
  'hyperextension.gif',
  'hyperextension-twist.gif',
  'incline-bench-pullover-cable.gif',
  'incline-push-up.gif',
  'incline-bicep-curl-dumbbell.gif',
  'incline-chest-fly-dumbbell.gif',
  'incline-chest-fly-machine.gif',
  'incline-chest-front-raise-cable.gif',
  'incline-chest-press-barbell.gif',
  'incline-chest-press-cable.gif',
  'incline-chest-press-dumbbell.gif',
  'incline-chest-press-smith-machine.gif',
  'incline-close-grip-chest-press-dumbbell.gif',
  'incline-front-raise-dumbbell.gif',
  'incline-leg-press.gif',
  'incline-shoulder-press-machine.gif',
  'incline-walk.gif',
  'inverted-rear-delt-row.gif',
  'jack-knife.gif',
  'jump-squat-trap-bar.gif',
  'jumping-rope.gif',
  'kettlebell-swing.gif',
  'kick-back-band.gif',
  'kick-back.gif',
  'knee-flexion.gif',
  'knee-push-ups.gif',
  'knee-to-chest-stretch.gif',
  'kneeling-cable-crunch.gif',
  'kneeling-cable-shoulder-press.gif',
  'kneeling-overhead-press-landmine.gif',
  'lat-pulldown-plateloaded.gif',
  'lat-pulldown.gif',
  'lateral-band-walk.gif',
  'lateral-raise-cable.gif',
  'lateral-raise-machine.gif',
  'lean-away-dumbbell-lateral-raise.gif',
  'leaning-lateral-raise-cable.gif',
  'leaning-lateral-raise.gif',
  'leg-abduction-cable.gif',
  'leg-adduction.gif',
  'leg-extension-glute-band.gif',
  'leg-extension.gif',
  'low-cable-squat.gif',
  'split-squat-smith-machine.gif',
  'lying-cable-reverse-fly.gif',
  'lying-down-leg-raise.gif',
  'lying-high-bicep-curl-cable.gif',
  'lying-leg-curl.gif',
  'mag-grip-lat-pulldown.gif',
  'march.gif',
  'medicine-ball-side-throw.gif',
  'military-press.gif',
  'mountain-climber.gif',
  'narrow-grip-shoulder-press-machine.gif',
  'nordic-hamstring-curl.gif',
  'one-arm-preacher-curl-dumbbell.gif',
  'overhead-bend-tricep-extension.gif',
  'overhead-biceps-curl-cable.gif',
  'overhead-side-bicep-curl-cable.gif',
  'overhead-squat-barbell.gif',
  'overhead-squat-dumbbell.gif',
  'overhead-tricep-cable-extension.gif',
  'overhead-tricep-extension-dumbbell.gif',
  'palloff-press.gif',
  'plank-jack.gif',
  'plank.gif',
  'plank-to-push-up.gif',
  'power-clean.gif',
  'preacher-curl-cable.gif',
  'preacher-curl-machine.gif',
  'prone-back-extension.gif',
  'prone-incline-barbell-curl.gif',
  'pull-through-cable.gif',
  'pull-up-machine-step-down.gif',
  'pull-up.gif',
  'pull-up-with-bench-support.gif',
  'pullover-barbell.gif',
  'pullover-dumbbell.gif',
  'push-press-dumbbell.gif',
  'push-up.gif',
  'push-ups-narrow.gif',
  'renegade-row.gif',
  'reverse-barbell-curl.gif',
  'reverse-crunch-bend-knee.gif',
  'reverse-crunch-straight-knee.gif',
  'reverse-fly-cable.gif',
  'reverse-fly-incline-bench-dumbbell.gif',
  'rear-delt-stretch.gif',
  'reverse-fly-machine.gif',
  'reverse-grip-chest-press-dumbbell.gif',
  'reverse-lunge-dumbbell.gif',
  'reverse-lunge-short-step.gif',
  'reverse-lunges-dumbbell.gif',
  'reverse-lunges-landmine.gif',
  'reverse-woodchop.gif',
  'reverse-wrist-curls.gif',
  'rollout.gif',
  'roman-chair-side-bend.gif',
  'romanian-deadlift-dumbbell.gif',
  'romanian-deadlift-landmine.gif',
  'rope-hammer-curl.gif',
  'rope-straight-back-pulldown.gif',
  'rotator-cuff.gif',
  'rowing-machine.gif',
  'run.gif',
  'russian-twist-cable.gif',
  'russian-twist-dumbbell.gif',
  'seatd-chest-press-cable.gif',
  'seated-calf-raise-machine.gif',
  'seated-chest-press-machine-plateloaded.gif',
  'seated-crunches-machine.gif',
  'seated-hip-abduction-machine.gif',
  'seated-knee-raise.gif',
  'seated-lateral-raise.gif',
  'seated-leg-curl-with-band.gif',
  'seated-leg-curl.gif',
  'seated-march.gif',
  'seated-overhead-press-dumbbell.gif',
  'seated-reverse-fly.gif',
  'seated-shoulder-press-dumbbell.gif',
  'shoulder-external-rotation-cable.gif',
  'shoulder-press-machine.gif',
  'shoulder-press-smith-machine.gif',
  'shrugs-dumbbell.gif',
  'shrugs-smith-machine.gif',
  'side-lunges-dumbbell.gif',
  'side-plank.gif',
  'side-toe-taps.gif',
  'single-arm-arnold-press.gif',
  'single-arm-behind-the-back-cable-curl.gif',
  'single-arm-chest-press-machine.gif',
  'single-arm-clean-and-press.gif',
  'single-arm-dumbbell-snatch.gif',
  'single-arm-high-cable-curl.gif',
  'single-arm-kneeling-pulldown-cable.gif',
  'single-arm-lat-pulldown.gif',
  'single-arm-over-head-tricep-extension.gif',
  'single-arm-prechear-curl-dumbbell.gif',
  'single-arm-reverse-tricep-cable.gif',
  'single-arm-reverse-tricep-extension-cable.gif',
  'single-arm-row-dumbbell.gif',
  'single-arm-upright-pulldown.gif',
  'single-dumbbell-bulgarian-split-squat.gif',
  'single-dumbbell-wrist-curl.gif',
  'single-leg-3-way-reach.gif',
  'single-leg-glute-bridges.gif',
  'single-leg-hip-extension-cable.gif',
  'single-leg-hip-thrust.gif',
  'single-leg-hip-thrust-barbell.gif',
  'single-leg-incline-leg-press.gif',
  'single-leg-lying-leg-curl.gif',
  'single-leg-romanian-deadlift.gif',
  'single-shoulder-press-dumbbell.gif',
  'sit-up.gif',
  'skierg.gif',
  'skull-crusher.gif',
  'sled-push.gif',
  'split-squat-barbell.gif',
  'squat-jump.gif',
  'squat-mobility.gif',
  'squat-smith-machine.gif',
  'stability-ball-leg-curl.gif',
  'stability-ball-side-crunch.gif',
  'stability-ball-wall-squat.gif',
  'stairmaster.gif',
  'staggered-stance-dumbbell-romanian-deadlift.gif',
  'standing-alternative-toe-touch.gif',
  'standing-cable-fly.gif',
  'standing-calf-raise-machine.gif',
  'standing-kick-back-glute-band.gif',
  'standing-leg-curl-glute-band.gif',
  'standing-single-arm-cable-row.gif',
  'step-down-cable.gif',
  'step-machine.gif',
  'step-up-dumbbell.gif',
  'stiff-leg-deadlift.gif',
  'straddle-stretch.gif',
  'straight-arm-lat-pulldown.gif',
  'straight-back-wide-grip-cable-row.gif',
  'sumo-high-pull.gif',
  'sumo-squat-dumbbell.gif',
  'superman-raise.gif',
  'swiss-ball-hyperextension.gif',
  'swiss-ball-knee-tuck.gif',
  'swiss-ball-rollout.gif',
  'toe-touch-crunch.gif',
  'tread-the-needle-stretch.gif',
  'tricep-extension-dumbbell.gif',
  'tricep-extension-seated-machine.gif',
  'tricep-kick-back-cable.gif',
  'tricep-kick-back-dumbbell.gif',
  'triceps-extension-with-dumbbells.gif',
  'triceps-push-down-v-grip-cable.gif',
  'triceps-rope-push-down.gif',
  'trx-standing-rollout.gif',
  'upright-row-barbell.gif',
  'upright-row-cable.gif',
  'upright-row-dumbbell.gif',
  'v-sit-up.gif',
  'walk.gif',
  'walking-lunges-dumbbell.gif',
  'wall-pushups.gif',
  'wall-squat.gif',
  'wide-grip-bendover-row-barbell.gif',
  'wide-grip-chest-supported-row-dumbbell.gif',
  'woodchop-cable.gif',
  'wrist-extension.gif',
  'wrist-flexion.gif',
  'y-raise-cable.gif',
  'zercher-squat-smith-machine.gif',
];

// eslint-disable-next-line no-unused-vars
const gifToExerciseName = (filename) =>
  filename
    .replace('.gif', '')
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const GifAudit = ({ onBack }) => {
  const { isAdmin, isSuperAdmin } = useUserRole();
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    withGif: 0,
    withoutGif: 0,
    validGif: 0,
    brokenGif: 0,
    unverifiedGif: 0,
    checkingGif: 0,
    duplicateGif: 0,
  });
  const [filter, setFilter] = useState('all'); // 'all', 'missing', 'broken', 'valid', 'duplicate'
  const [fixing, setFixing] = useState(false);
  const [verifying, setVerifying] = useState(false); // GIF file checks still running in background
  const [gifPickerOpen, setGifPickerOpen] = useState(null); // exercise ID of open picker
  const [gifSearch, setGifSearch] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, fixableCount: 0 });
  const [pendingFixable, setPendingFixable] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadExercises();
    return () => { mountedRef.current = false; };
  }, []);

  const loadExercises = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('exercises').select('*').order('name');
      if (error) throw error;
      if (!mountedRef.current) return;

      // Render immediately from the DB. GIF file verification is a HEAD per
      // distinct address and Storage rate-limits bursts — so it must NOT
      // gate the screen (that's what wedged it on "Loading…"). Rows show as
      // 'checking' until the background pass below resolves them.
      const first = analyzeGifAudit(data, {});
      setExercises(first.analyzedWithDupes);
      setStats(first.stats);
      setLoading(false);

      // Storage RLS blocks listing the bucket for non-admins, but the public
      // bucket flag lets unauthenticated HEAD requests through — verify each
      // distinct address that way rather than trusting it by shape. That goes
      // for an address on the app too: the app answers a missing file with its
      // own front page, so only a picture counts (see utils/gifAuditChecks.js).
      const storageUrls = [...new Set(
        (data || [])
          .map(ex => ex.gif_url)
          .filter(url => url && url.trim() !== '')
      )];
      if (storageUrls.length === 0) return;

      setVerifying(true);

      // Hard wall-clock cap: after this, stop probing and leave the rest
      // 'unverified' rather than hanging while Storage 429s everything.
      const deadline = Date.now() + 45000;
      const checkStorageUrl = (url) => checkDemoAddress(url, { deadline });

      const storageStateByUrl = {};
      const CONCURRENCY = 6;
      for (let i = 0; i < storageUrls.length; i += CONCURRENCY) {
        const batch = storageUrls.slice(i, i + CONCURRENCY);
        const results = await Promise.all(batch.map(u => checkStorageUrl(u)));
        batch.forEach((u, j) => { storageStateByUrl[u] = results[j]; });
      }
      if (!mountedRef.current) return;

      const final = analyzeGifAudit(data, storageStateByUrl);
      setExercises(final.analyzedWithDupes);
      setStats(final.stats);
      setVerifying(false);
    } catch (err) {
      console.error('GifAudit: Error loading exercises:', err);
      toastError('Error loading exercises. Please try again.');
      if (mountedRef.current) { setLoading(false); setVerifying(false); }
    }
  };

  // Try to find a matching GIF for an exercise name
  const findMatchingGif = (exerciseName) => {
    const normalized = exerciseName
      .toLowerCase()
      .replace(/['']/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');

    // Direct match
    const directMatch = AVAILABLE_GIFS.find(gif =>
      gif.replace('.gif', '') === normalized
    );
    if (directMatch) return storageDemoAddress(directMatch);

    // Fuzzy match - check if GIF name contains exercise name or vice versa
    const fuzzyMatch = AVAILABLE_GIFS.find(gif => {
      const gifName = gif.replace('.gif', '');
      return gifName.includes(normalized) || normalized.includes(gifName);
    });
    if (fuzzyMatch) return storageDemoAddress(fuzzyMatch);

    return null;
  };

  const handleAutoFix = () => {
    const missingExercises = exercises.filter(e => e.status === 'missing' || e.status === 'broken');
    const fixable = [];

    for (const ex of missingExercises) {
      const match = findMatchingGif(ex.name);
      if (match) {
        fixable.push({ id: ex.id, name: ex.name, gif_url: match });
      }
    }

    if (fixable.length === 0) {
      toastInfo('No automatic matches found. You may need to manually assign GIFs.');
      return;
    }

    setPendingFixable(fixable);
    setConfirmDialog({ isOpen: true, fixableCount: fixable.length });
  };

  // Saves an address only after the file has answered as a picture, and only
  // counts it saved when a row came back: an update the access rules refuse
  // changes nothing and reports no error.
  const saveDemoAddress = async (exerciseId, gifUrl) => {
    if ((await checkDemoAddress(gifUrl)) !== 'ok') {
      const err = new Error('The demo file did not load');
      err.demoNotThere = true;
      throw err;
    }
    const rows = assertWriteOk('Saving the demo address', await supabase
      .from('exercises')
      .update({ gif_url: gifUrl })
      .eq('id', exerciseId)
      .select('id'));
    if (!rows || rows.length === 0) throw new Error('Saving the demo address: no row was changed');
  };

  const handleConfirmAutoFix = async () => {
    setFixing(true);
    let fixed = 0;
    let failed = 0;

    for (const item of pendingFixable) {
      try {
        await saveDemoAddress(item.id, item.gif_url);
        fixed++;
      } catch (err) {
        console.error(`GifAudit: Failed to update ${item.name}:`, err);
        failed++;
      }
    }

    setFixing(false);
    setPendingFixable([]);

    if (failed > 0) {
      toastError(`Updated ${fixed} exercises, but ${failed} failed.`);
    } else {
      toastSuccess(`Updated ${fixed} exercises successfully!`);
    }
    loadExercises();
  };

  const handleManualFix = async (exerciseId, gifUrl) => {
    try {
      await saveDemoAddress(exerciseId, gifUrl);
      toastSuccess('GIF updated successfully!');
      loadExercises();
    } catch (err) {
      console.error('GifAudit: Error updating GIF:', err);
      toastError(err.demoNotThere ? 'That GIF did not load, so nothing was changed.' : 'Error updating GIF. Please try again.');
    }
  };

  const getFilteredExercises = () => {
    if (filter === 'all') return exercises;
    if (filter === 'duplicate') return exercises.filter(e => e.isDuplicate);
    return exercises.filter(e => e.status === filter);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return theme.success;
      case 'missing': return '#E67E22';
      case 'broken': return theme.danger;
      case 'unverified': return theme.textMuted;
      case 'checking': return theme.textMuted;
      case 'duplicate': return '#9B59B6';
      default: return theme.textMuted;
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '40px',
        backgroundColor: theme.bg,
        minHeight: '100vh',
        color: theme.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        Loading exercises...
      </div>
    );
  }

  return (
    <div style={{
      padding: '40px',
      backgroundColor: theme.bg,
      minHeight: '100vh',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
      }}>
        <div>
          <button
            onClick={onBack}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${theme.gold}`,
              color: theme.gold,
              padding: '10px 18px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '10px',
            }}
          >
            ← Back to Admin
          </button>
          <h1 style={{ color: theme.gold, fontSize: '28px', margin: 0 }}>
            GIF Audit Tool
          </h1>
        </div>
        {(isAdmin || isSuperAdmin) && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleAutoFix}
              disabled={fixing}
              style={{
                backgroundColor: theme.gold,
                color: theme.bg,
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: fixing ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                opacity: fixing ? 0.5 : 1,
              }}
            >
              {fixing ? 'Fixing...' : 'Auto-Fix Missing GIFs'}
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '16px',
        marginBottom: '30px',
      }}>
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '14px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '32px', fontWeight: 700, color: theme.text }}>
            {stats.total}
          </div>
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>Total Exercises</div>
        </div>
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '14px',
          textAlign: 'center',
          cursor: 'pointer',
          border: filter === 'valid' ? `2px solid ${theme.success}` : '2px solid transparent',
        }}
        onClick={() => setFilter(filter === 'valid' ? 'all' : 'valid')}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: theme.success }}>
            {stats.validGif}
          </div>
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>Valid GIFs</div>
        </div>
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '14px',
          textAlign: 'center',
          cursor: 'pointer',
          border: filter === 'missing' ? '2px solid #E67E22' : '2px solid transparent',
        }}
        onClick={() => setFilter(filter === 'missing' ? 'all' : 'missing')}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#E67E22' }}>
            {stats.withoutGif}
          </div>
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>Missing GIFs</div>
        </div>
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '14px',
          textAlign: 'center',
          cursor: 'pointer',
          border: filter === 'broken' ? `2px solid ${theme.danger}` : '2px solid transparent',
        }}
        onClick={() => setFilter(filter === 'broken' ? 'all' : 'broken')}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: theme.danger }}>
            {stats.brokenGif}
          </div>
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>Broken Links</div>
        </div>
        <div style={{
          backgroundColor: theme.surface1,
          padding: '20px',
          borderRadius: '14px',
          textAlign: 'center',
          cursor: 'pointer',
          border: filter === 'duplicate' ? '2px solid #9B59B6' : '2px solid transparent',
        }}
        onClick={() => setFilter(filter === 'duplicate' ? 'all' : 'duplicate')}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#9B59B6' }}>
            {stats.duplicateGif}
          </div>
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>Duplicate GIFs</div>
        </div>
      </div>

      {/* Filter Info */}
      {filter !== 'all' && (
        <div style={{
          backgroundColor: theme.surface1,
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: theme.text }}>
            Showing: <strong style={{ color: getStatusColor(filter) }}>{filter}</strong> exercises ({getFilteredExercises().length})
            {filter === 'duplicate' && ' — these exercises share a GIF with at least one other exercise'}
          </span>
          <button
            onClick={() => setFilter('all')}
            style={{
              background: 'none',
              border: `1px solid ${theme.textMuted}`,
              color: theme.textMuted,
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Exercise List */}
      <div style={{
        backgroundColor: theme.surface1,
        borderRadius: '14px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: theme.surface3 }}>
              <th style={{ padding: '16px', textAlign: 'left', color: theme.gold }}>Exercise</th>
              <th style={{ padding: '16px', textAlign: 'left', color: theme.gold }}>Category</th>
              <th style={{ padding: '16px', textAlign: 'left', color: theme.gold }}>GIF URL</th>
              <th style={{ padding: '16px', textAlign: 'center', color: theme.gold }}>Status</th>
              <th style={{ padding: '16px', textAlign: 'center', color: theme.gold }}>Preview</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredExercises().map((ex, idx) => (
              <tr
                key={ex.id}
                style={{
                  borderBottom: `1px solid ${theme.surface3}`,
                  backgroundColor: idx % 2 === 0 ? theme.surface1 : '#0D1229',
                }}
              >
                <td style={{ padding: '12px 16px', color: theme.text }}>{ex.name}</td>
                <td style={{ padding: '12px 16px', color: theme.textMuted }}>{ex.movement_category || ex.muscle_group || '-'}</td>
                <td style={{ padding: '12px 16px', color: theme.textMuted, fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ex.gif_url || '-'}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      backgroundColor: `${getStatusColor(ex.status)}20`,
                      color: getStatusColor(ex.status),
                    }}>
                      {ex.status.toUpperCase()}
                    </span>
                    {ex.isDuplicate && (
                      <span
                        title={`Also used by: ${ex.duplicateWith.join(', ')}`}
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '14px',
                          fontSize: '11px',
                          fontWeight: 700,
                          backgroundColor: '#9B59B620',
                          color: '#9B59B6',
                          cursor: 'help',
                        }}
                      >
                        DUPLICATE
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {ex.status === 'valid' && !ex.isDuplicate && (
                    <ExerciseStill
                      gifUrl={ex.gif_url}
                      alt={ex.name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  )}
                  {ex.status === 'valid' && ex.isDuplicate && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', position: 'relative' }}>
                      <ExerciseStill
                        gifUrl={ex.gif_url}
                        alt={ex.name}
                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '2px solid #9B59B6' }}
                      />
                      {(isAdmin || isSuperAdmin) && (
                      <button
                        onClick={() => {
                          setGifPickerOpen(gifPickerOpen === ex.id ? null : ex.id);
                          setGifSearch('');
                        }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#9B59B620',
                          color: '#9B59B6',
                          border: '1px solid #9B59B6',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        Reassign
                      </button>
                      )}
                      {(isAdmin || isSuperAdmin) && gifPickerOpen === ex.id && (
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: '4px',
                          width: '280px',
                          maxHeight: '300px',
                          backgroundColor: theme.surface1,
                          border: `1px solid ${theme.surface3}`,
                          borderRadius: '8px',
                          zIndex: 1000,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                          overflow: 'hidden',
                        }}>
                          <div style={{ padding: '8px', borderBottom: `1px solid ${theme.surface3}` }}>
                            <input
                              type="text"
                              placeholder="Search GIFs..."
                              value={gifSearch}
                              onChange={(e) => setGifSearch(e.target.value)}
                              autoFocus
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: `1px solid ${theme.surface3}`,
                                backgroundColor: theme.bg,
                                color: theme.text,
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                            {AVAILABLE_GIFS
                              .filter(gif => gif.toLowerCase().includes(gifSearch.toLowerCase()))
                              .map(gif => (
                                <div
                                  key={gif}
                                  onClick={() => {
                                    handleManualFix(ex.id, storageDemoAddress(gif));
                                    setGifPickerOpen(null);
                                    setGifSearch('');
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    borderBottom: `1px solid ${theme.surface3}`,
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surface3}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <img
                                    src={storageDemoStill(gif)}
                                    loading="lazy"
                                    alt={gif}
                                    style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <span style={{ color: theme.text, fontSize: '12px', flex: 1 }}>
                                    {gif.replace('.gif', '').replace(/-/g, ' ')}
                                  </span>
                                </div>
                              ))}
                            {AVAILABLE_GIFS.filter(gif => gif.toLowerCase().includes(gifSearch.toLowerCase())).length === 0 && (
                              <div style={{ padding: '12px', color: theme.textMuted, textAlign: 'center', fontSize: '12px' }}>
                                No GIFs found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {ex.status !== 'valid' && (isAdmin || isSuperAdmin) && (
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => {
                          setGifPickerOpen(gifPickerOpen === ex.id ? null : ex.id);
                          setGifSearch('');
                        }}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '4px',
                          backgroundColor: theme.surface3,
                          color: theme.text,
                          border: 'none',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        Assign GIF...
                      </button>
                      {gifPickerOpen === ex.id && (
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: '4px',
                          width: '280px',
                          maxHeight: '300px',
                          backgroundColor: theme.surface1,
                          border: `1px solid ${theme.surface3}`,
                          borderRadius: '8px',
                          zIndex: 1000,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                          overflow: 'hidden',
                        }}>
                          <div style={{ padding: '8px', borderBottom: `1px solid ${theme.surface3}` }}>
                            <input
                              type="text"
                              placeholder="Search GIFs..."
                              value={gifSearch}
                              onChange={(e) => setGifSearch(e.target.value)}
                              autoFocus
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: `1px solid ${theme.surface3}`,
                                backgroundColor: theme.bg,
                                color: theme.text,
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                            {AVAILABLE_GIFS
                              .filter(gif => gif.toLowerCase().includes(gifSearch.toLowerCase()))
                              .map(gif => (
                                <div
                                  key={gif}
                                  onClick={() => {
                                    handleManualFix(ex.id, storageDemoAddress(gif));
                                    setGifPickerOpen(null);
                                    setGifSearch('');
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    borderBottom: `1px solid ${theme.surface3}`,
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surface3}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <img
                                    src={storageDemoStill(gif)}
                                    loading="lazy"
                                    alt={gif}
                                    style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <span style={{ color: theme.text, fontSize: '12px', flex: 1 }}>
                                    {gif.replace('.gif', '').replace(/-/g, ' ')}
                                  </span>
                                </div>
                              ))}
                            {AVAILABLE_GIFS.filter(gif => gif.toLowerCase().includes(gifSearch.toLowerCase())).length === 0 && (
                              <div style={{ padding: '12px', color: theme.textMuted, textAlign: 'center', fontSize: '12px' }}>
                                No GIFs found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: theme.surface1,
        borderRadius: '8px',
        color: theme.textMuted,
        fontSize: '14px',
      }}>
        <strong>Summary:</strong> {stats.validGif} of {stats.total} exercises have valid GIFs ({stats.total ? Math.round((stats.validGif / stats.total) * 100) : 0}% coverage).
        {verifying && ` Verifying ${stats.checkingGif} GIF file${stats.checkingGif === 1 ? '' : 's'} in the background…`}
        {stats.withoutGif > 0 && ` ${stats.withoutGif} exercises need GIF assignments.`}
        {stats.brokenGif > 0 && ` ${stats.brokenGif} exercises have broken GIF links.`}
        {!verifying && stats.unverifiedGif > 0 && ` ${stats.unverifiedGif} could not be verified (Storage rate-limited the check) — reload to retry.`}
        {stats.duplicateGif > 0 && ` ${stats.duplicateGif} exercises share a GIF with another exercise — click "Duplicate GIFs" to review.`}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, fixableCount: 0 })}
        onConfirm={handleConfirmAutoFix}
        title="Auto-fix GIFs"
        message={`Found ${confirmDialog.fixableCount} potential GIF matches. Update them now?`}
        confirmText="Update All"
        variant="warning"
      />
    </div>
  );
};

export default GifAudit;
